import React from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useAuth } from '../../hooks';
import { useHasPermission } from '../../helpers/api/auth/index';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';

/**
 * A route that requires a login, and optionally a permission.
 *
 * `permission` is one of PERMISSIONS — a string matched against the array on the admin
 * JWT, which is the `adminUsers` document's own `permissions`. ADMIN satisfies any of
 * them, so gating on a named permission still lets superusers through while leaving room
 * to grant one without making somebody a superuser.
 *
 * Enforced HERE rather than only by hiding the nav link, because a hidden link is not
 * access control — the URL is still typeable, and these pages write driver state.
 *
 * Denied lands on /home rather than /login: they are signed in, just not entitled, and
 * bouncing them to a login form would read as a session problem.
 *
 * NOTE: this is a UI affordance, not a security boundary. The admin API's own guard only
 * validates the JWT — it checks no permissions at all — so anyone holding any admin
 * token can call these endpoints directly. Real enforcement has to land server-side.
 */
const PrivateRoute = ({ component: Component, permission, ...rest }) => {
  const auth = useAuth();
  // Hooks cannot be called conditionally, so this always runs. The sentinel is only
  // READ when a permission was actually asked for, so an absent `permission` can never
  // accidentally open a gate.
  const allowed = useHasPermission(permission || PERMISSIONS.ADMIN);

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!auth.token) {
          return <Redirect to={{ pathname: '/login', state: { from: props.location } }} />;
        }
        if (permission && !allowed) {
          return <Redirect to="/home" />;
        }
        return <Component {...props} />;
      }}
    />
  );
};

export default PrivateRoute;
